/**
 * pokedex-complete-dex — pure derivation for the "Complete" Pokédex.
 *
 * The Complete dex has NO scan path and NO manual-edit path: a species' state
 * is COMPUTED from the other dexes, never stored. A species counts as
 * "complete" when it is caught in every dex that applies to it:
 *
 *   - Always required: Shiny, Perfect (100%), XXL, XXS, Lucky.
 *   - Conditionally required: Shadow and/or Purified, but ONLY when that
 *     species is a member of the respective GO-availability roster. A species
 *     with no Shadow release can be complete on the five base dexes alone.
 *
 * Because the Shadow/Purified rosters grow over time (Niantic releases new
 * Shadow/Purified species), a species that was "complete" can revert to
 * incomplete the moment it enters the Shadow or Purified roster and has not yet
 * been registered there — exactly the behaviour the product asks for. That
 * falls out naturally here: `requiredCompleteVariants` re-reads roster
 * membership on every derivation, so a newly-rostered species immediately
 * starts requiring the new dex.
 *
 * This module is intentionally dependency-light and side-effect free so it can
 * be unit-tested in isolation. The dex-state store wires it to the real store
 * (present reads) and the live rosters (curatedRosterFor).
 */
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

/** The five whole-species dexes every complete candidate must satisfy. */
export const COMPLETE_BASE_VARIANTS: readonly PokedexVariant[] = [
  "shiny",
  "perfect",
  "xxl",
  "xxs",
  "lucky",
];

/**
 * The roster-gated dexes: required only when the species is a member of the
 * matching curated roster. Order pairs each variant with the roster that gates
 * it, consumed by `requiredCompleteVariants`.
 */
export const COMPLETE_CONDITIONAL_VARIANTS: readonly PokedexVariant[] = [
  "shadow",
  "purified",
];

/** Membership tests for the roster-gated dexes, injected by the caller. */
export interface CompleteRosterMembership {
  hasShadow: (dexNumber: number) => boolean;
  hasPurified: (dexNumber: number) => boolean;
}

/**
 * The exact set of dexes a species must be caught in to count as complete —
 * the five base dexes plus Shadow/Purified when (and only when) the species is
 * rostered for them.
 */
export function requiredCompleteVariants(
  dexNumber: number,
  rosters: CompleteRosterMembership,
): PokedexVariant[] {
  const required = [...COMPLETE_BASE_VARIANTS];
  if (rosters.hasShadow(dexNumber)) required.push("shadow");
  if (rosters.hasPurified(dexNumber)) required.push("purified");
  return required;
}

/** All inputs the complete-set derivation needs, snapshotted by the caller. */
export interface CompleteDerivationInput extends CompleteRosterMembership {
  /**
   * Present-by-variant snapshot: for each contributing variant, the set of dex
   * numbers currently caught (state === "present") under that variant. Missing
   * keys are treated as empty sets.
   */
  presentByVariant: Map<PokedexVariant, ReadonlySet<number>>;
}

/** True when a single species satisfies every dex that applies to it. */
export function isSpeciesComplete(
  dexNumber: number,
  input: CompleteDerivationInput,
): boolean {
  const required = requiredCompleteVariants(dexNumber, input);
  for (const variant of required) {
    const present = input.presentByVariant.get(variant);
    if (!present || !present.has(dexNumber)) return false;
  }
  return true;
}

/**
 * The set of every dex number that is currently complete.
 *
 * We only ever need to test species that are already present in ALL five base
 * dexes, so the candidate set is the intersection of the base present-sets —
 * seeded from the smallest one for efficiency. Each surviving candidate is then
 * checked against its roster-gated requirements.
 */
export function computeCompletePresentSet(
  input: CompleteDerivationInput,
): Set<number> {
  const baseSets = COMPLETE_BASE_VARIANTS.map(
    (v) => input.presentByVariant.get(v) ?? EMPTY_SET,
  );
  // If any base dex has zero catches, nothing can be complete.
  if (baseSets.some((s) => s.size === 0)) return new Set();

  // Seed the candidate walk from the smallest base set — a species missing from
  // it can never be complete, so this is the tightest starting point.
  const seed = baseSets.reduce((a, b) => (a.size <= b.size ? a : b));
  const result = new Set<number>();
  seed.forEach((dexNumber) => {
    if (isSpeciesComplete(dexNumber, input)) result.add(dexNumber);
  });
  return result;
}

const EMPTY_SET: ReadonlySet<number> = new Set<number>();
